import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '../config/support';
import { SITE_CONFIG, officialSocialChannelLabels, getExchangeHref, getExchangeStatusLabel, getExchangeUrlDisplay } from '@/config/site';
import { STAKING_EARLY_WITHDRAW_RULE, STAKING_REWARDS_WITH_RULE } from '@/lib/stakingRules';

const EXCHANGE_URL = getExchangeHref();
const EXCHANGE_URL_LABEL = getExchangeUrlDisplay();

const faqs = [
  {
    question: 'What is BITZX ($BZX)?',
    answer: 'BITZX is a utility token built on BNB Chain (BEP-20). It is presented as the foundation of the BITZX ecosystem and is intended to support future platform utility, ecosystem access, and project growth initiatives.',
  },
  {
    question: 'How can I buy $BZX?',
    answer: 'You can buy $BZX on PancakeSwap. Simply connect your wallet (MetaMask or Trust Wallet), ensure you have BNB for the swap and gas fees, then paste the $BZX contract address and swap your BNB for $BZX tokens. Set slippage to 5-10% for best results.',
  },
  {
    question: 'What is the total supply of $BZX?',
    answer: 'The total supply of $BZX is 1,000,000,000 (1 billion) tokens. The allocation includes 40% for liquidity, 15% for marketing, 15% for development, 15% for ecosystem rewards, 10% for team/reserve, and 5% for partnerships.',
  },
  {
    question: 'What is BITZX Exchange?',
    answer: `BITZX Exchange is the project's live centralized trading platform at ${EXCHANGE_URL_LABEL}. Trade BZX and other spot pairs with professional charts, secure wallets, and platform utility for $BZX holders.`,
  },
  {
    question: 'Is BITZX Exchange live?',
    answer: `Yes. BITZX Exchange is ${getExchangeStatusLabel().toLowerCase()} at ${EXCHANGE_URL_LABEL}. Sign up, complete KYC where required, and start trading through the official site.`,
  },
  {
    question: 'Can I deposit and withdraw in INR (Indian Rupees)?',
    answer: `INR deposits and payouts are available on BITZX Exchange for eligible Indian users. Sign in at ${EXCHANGE_URL_LABEL}, complete KYC where required, and manage INR flows under Wallet → History → INR history.`,
  },
  {
    question: 'Is $BZX safe? Has it been audited?',
    answer: 'Always verify the official contract address and rely only on published audit or security documents that the project has made public. If audit reports are not yet published on the official website or whitepaper, they should not be assumed.',
  },
  {
    question: 'What benefits do $BZX holders get?',
    answer: 'Holding $BZX provides numerous benefits including: trading fee discounts on BITZX Exchange, VIP membership tiers, staking rewards, referral bonuses, early access to launchpad projects, and exclusive ecosystem features.',
  },
  {
    question: 'What happens if I withdraw from staking early?',
    answer: STAKING_EARLY_WITHDRAW_RULE + ' Rewards and other staking benefits are paid only if you stay locked until the selected maturity date.',
  },
  {
    question: 'How does BITZX staking work?',
    answer: STAKING_REWARDS_WITH_RULE + ` Staking is available in the BITZX app and on BITZX Exchange at ${EXCHANGE_URL_LABEL}.`,
  },
  {
    question: 'How can I contact the team?',
    answer: (
      <>
        You can reach us through our official social channels
        {officialSocialChannelLabels().length > 0
          ? ` (${officialSocialChannelLabels().join(', ')})`
          : ''}{' '}
        — links are in the footer.
        For contact and support by email, write to{' '}
        <a
          href={SUPPORT_MAILTO}
          className="text-[#EBD38D] font-medium underline underline-offset-2 hover:text-white"
          data-testid="faq-support-email"
        >
          {SUPPORT_EMAIL}
        </a>
        {' '}(opens your default email app).
      </>
    ),
  },
];

export const FAQ = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="faq"
      ref={ref}
      className="section-padding relative overflow-hidden"
      data-testid="faq-section"
    >
      {/* Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9C7941]/5 rounded-full blur-3xl" />

      <div className="max-w-5xl mx-auto px-6 md:px-10 xl:px-16 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#EBD38D] text-sm font-semibold tracking-wider uppercase mb-4">
            FAQ
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-[#D5D5D0] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Find answers to common questions about BITZX and our ecosystem
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="card-glass border-none px-6 data-[state=open]:border-[#9C7941]/50"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-white hover:text-[#EBD38D] text-left py-6 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#8A8B90] pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-[#8A8B90] mb-4">
            Still have questions?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <a
              href={SITE_CONFIG.community.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#EBD38D] hover:underline font-medium"
              data-testid="faq-contact-link"
            >
              Join our Telegram community for support →
            </a>
            <a
              href={SUPPORT_MAILTO}
              className="text-[#EBD38D] hover:underline font-medium"
              data-testid="faq-email-link"
            >
              Email us at {SUPPORT_EMAIL} →
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
